import React, { useState } from 'react'
import { AppBar, Toolbar, Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthUser, useAuthActions } from '@/contexts/AuthContext'
import { useAdminDrawer } from '@/contexts/AdminDrawerContext'
import { useTopNavigationProgress } from './hooks/useTopNavigationProgress'
import { useNavigationConfig } from './hooks/useNavigationConfig'
import { useNavigationUtils } from './hooks/useNavigationUtils'
import { useResumeHandler } from './hooks/useResumeHandler'
import { useUsageQuotaContext, isLlmLockedFor } from '@/contexts/UsageQuotaContext'
import { NavigationLogo } from './components/NavigationLogo'
import { NavigationButtons } from './components/NavigationButtons'
// Lazy: only rendered for authenticated users, so anonymous visitors don't
// download the avatar/menu code (TRA-115).
const UserMenu = React.lazy(() =>
    import('./components/UserMenu').then((m) => ({ default: m.UserMenu }))
)
// MobileDrawer stays static: splitting it out moves shared MUI modules
// (Modal, List, ...) into extra lazy chunks that the public form pages then
// fetch as additional requests, which measurably hurt their LCP (TRA-115).
import { MobileDrawer } from './components/MobileDrawer'

interface TopNavigationProps {
    variant?: 'full' | 'simplified'
    showAuthLinks?: boolean
}

const TopNavigation: React.FC<TopNavigationProps> = ({
    variant = 'full',
    showAuthLinks = false,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t } = useTranslation()
    const user = useAuthUser()
    const { logout } = useAuthActions()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const { mostRecentStory, progressLoading } = useTopNavigationProgress(user)
    const { usage: usageQuota } = useUsageQuotaContext()
    const isLlmLocked = isLlmLockedFor(usageQuota)
    const { navigationItems, authItems } = useNavigationConfig()
    const { isActive, isLessonPage } = useNavigationUtils({ location })
    const { handleResumeClick } = useResumeHandler({
        mostRecentStory,
        onClose: () => setMobileDrawerOpen(false),
        navigate,
    })
    const { toggleDrawer: toggleAdminDrawer } = useAdminDrawer()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

    // Check if we're on an admin route
    const isAdminRoute = location.pathname.startsWith('/admin')

    const handleLogoClick = () => void navigate(user ? '/dashboard' : '/')
    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
        setAnchorEl(event.currentTarget)
    const handleUserMenuClose = () => setAnchorEl(null)
    const handleLogout = () => {
        // Navigate first, then logout, to prevent ProtectedRoute from overwriting loggedOut state
        void navigate('/login', {
            state: { loggedOut: true },
            replace: true,
        })
        logout()
        handleUserMenuClose()
        setMobileDrawerOpen(false)
    }
    const handleMobileDrawerToggle = () => setMobileDrawerOpen(!mobileDrawerOpen)
    const handleNavigation = (path: string) => {
        void navigate(path)
        setMobileDrawerOpen(false)
    }

    return (
        <>
            <AppBar
                position="static"
                elevation={1}
                sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}
            >
                <Toolbar>
                    {/* Admin drawer toggle button - shown on all admin routes */}
                    {isAdminRoute && (
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label={t('admin:nav.openDrawer')}
                            onClick={toggleAdminDrawer}
                            sx={{ mr: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <NavigationLogo user={user} onLogoClick={handleLogoClick} />

                    <Box sx={{ flexGrow: 1 }} />

                    {isMobile ? (
                        <IconButton
                            size="large"
                            aria-label={t('navigation:menu')}
                            aria-controls="mobile-menu"
                            aria-haspopup="true"
                            onClick={handleMobileDrawerToggle}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        <>
                            <NavigationButtons
                                variant={variant}
                                showAuthLinks={showAuthLinks}
                                user={user}
                                navigationItems={navigationItems}
                                authItems={authItems}
                                isActive={isActive}
                                isLessonPage={isLessonPage}
                                onNavigation={handleNavigation}
                                onResumeClick={handleResumeClick}
                                mostRecentStory={mostRecentStory}
                                progressLoading={progressLoading}
                                isLlmLocked={isLlmLocked}
                            />
                            {user && (
                                <React.Suspense fallback={null}>
                                    <UserMenu
                                        user={user}
                                        anchorEl={anchorEl}
                                        onMenuOpen={handleUserMenuOpen}
                                        onMenuClose={handleUserMenuClose}
                                        onNavigation={handleNavigation}
                                        onLogout={handleLogout}
                                    />
                                </React.Suspense>
                            )}
                        </>
                    )}
                </Toolbar>
            </AppBar>

            <MobileDrawer
                open={mobileDrawerOpen}
                onClose={handleMobileDrawerToggle}
                user={user}
                navigationItems={navigationItems}
                authItems={authItems}
                isActive={isActive}
                isLessonPage={isLessonPage}
                onNavigation={handleNavigation}
                onResumeClick={handleResumeClick}
                onLogout={handleLogout}
                mostRecentStory={mostRecentStory}
                progressLoading={progressLoading}
                isLlmLocked={isLlmLocked}
            />
        </>
    )
}

export default TopNavigation
